<template>
  <div
    class="mt-2"
    v-if="
      studyEnvironments &&
      studyEnvironments.length > 0 &&
      institution &&
      institution.internal_use_id == 'duoc.cl'
    "
  >
    <div class="d-flex">
      <div class="align-self-center">
        <strong>Estudiantes por ambiente:</strong>
      </div>
    </div>
    <div v-if="isLoading">
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <b-form-group
            v-for="n in studyEnvironments.length"
            :key="n"
            label-cols="0"
            label-cols-sm="3"
            class="label mt-2"
          >
            <template v-slot:label>
              <b-skeleton width="40%"></b-skeleton>
            </template>
            <b-skeleton type="input"></b-skeleton>
          </b-form-group>
        </template>
      </b-skeleton-wrapper>
    </div>
    <div v-else>
      <b-form-group
        v-for="environment in studyEnvironments"
        :key="environment.id"
        label-cols="0"
        label-cols-sm="3"
        :label-for="`input-suggested_students-${environment.id}`"
        class="label mt-2"
      >
        <template v-slot:label>
          <span class="environment-name"> N° Est. {{ environment.name }} </span>
        </template>
        <b-form-input
          :id="`input-suggested_students-${environment.id}`"
          type="number"
          min="0"
          :max="matterSelected.suggested_students"
          :value="getEnvironmentStudents(environment.id)"
          @input="updateEnvironmentStudents(environment.id, $event)"
          :disabled="matterSelected.is_closed"
          :state="validateState(environment.id)"
          :aria-describedby="`input-suggested_students-feedback-${environment.id}`"
          size="sm"
        ></b-form-input>
        <b-form-invalid-feedback
          :id="`input-suggested_students-feedback-${environment.id}`"
          >Este campo debe ser mayor o igual a 0 y no superar los
          {{ matterSelected.suggested_students }}
          estudiantes.</b-form-invalid-feedback
        >
      </b-form-group>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { minValue, maxValue } from "vuelidate/lib/validators";

export default {
  name: "MatterStudyEnvironment",
  mixins: [validationMixin],
  components: {},
  props: {
    old_matter: {
      type: Object,
      required: false,
    },
    matter: {
      type: Object,
      required: false,
    },
    studyEnvironments: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      all_env_students_old_matter: [], // Copia original del backend para detectar eliminaciones
      all_env_students_matter: [], // Copia original del backend para detectar eliminaciones
      local_env_students_old_matter: [], // Datos locales editables
      local_env_students_matter: [], // Datos locales editables
      isLoading: true,
    };
  },
  computed: {
    ...mapGetters({
      institution: "getInstitution",
      matter_study_environments: names.MATTER_STUDY_ENVIRONMENTS,
      egress_profile_matter_study_environments:
        names.EGRESS_PROFILE_MATTER_STUDY_ENVIRONMENTS,
    }),
    matterSelected() {
      return this.old_matter || this.matter;
    },
    env_students_old_matter() {
      if (!this.old_matter) return [];
      return this.matter_study_environments.filter(
        (x) => x.matter == this.old_matter.id
      );
    },
    env_students_matter() {
      if (!this.matter) return [];
      return this.egress_profile_matter_study_environments.filter(
        (x) => x.egress_profile_matter == this.matter.id
      );
    },
  },
  validations() {
    const validations = {};
    this.studyEnvironments.forEach((env) => {
      validations[`env_${env.id}`] = {
        minValue: minValue(0),
        maxValue: maxValue(this.matterSelected.suggested_students || 0),
      };
    });
    return validations;
  },
  methods: {
    validateState(environmentId) {
      const value = this.getEnvironmentStudents(environmentId);
      const max = this.matterSelected.suggested_students || 0;

      if (value < 0 || value > max) {
        return false;
      }
      return null;
    },
    validateAllEnvironments() {
      // Retorna true si todas las validaciones son correctas, false si hay errores
      const max = this.matterSelected.suggested_students || 0;
      const dataArray = this.matter
        ? this.local_env_students_matter
        : this.local_env_students_old_matter;

      for (const env of dataArray) {
        if (env.suggested_students < 0 || env.suggested_students > max) {
          return false;
        }
      }
      return true;
    },
    getEnvironmentStudents(environmentId) {
      const dataArray = this.matter
        ? this.local_env_students_matter
        : this.local_env_students_old_matter;
      const env = dataArray.find(
        (item) => item.study_environment == environmentId
      );
      return env ? env.suggested_students : 0;
    },
    updateEnvironmentStudents(environmentId, value) {
      const dataArray = this.matter
        ? this.local_env_students_matter
        : this.local_env_students_old_matter;
      const existing = dataArray.find(
        (item) => item.study_environment === environmentId
      );

      const suggested_students = parseInt(value) || 0;

      if (existing) {
        // Actualizar registro existente
        existing.suggested_students = suggested_students;

        // Solo hacer PATCH si el registro ya tiene ID (ya existe en backend)
        if (existing.id) {
          const payload = {
            id: existing.id,
            data: {
              ...existing,
              suggested_students,
            },
          };

          if (this.old_matter) {
            this.$store.dispatch(names.PATCH_MATTER_STUDY_ENVIRONMENT, payload);
          } else if (this.matter) {
            this.$store.dispatch(
              names.PATCH_EGRESS_PROFILE_MATTER_STUDY_ENVIRONMENT,
              payload
            );
          }
        }
      } else {
        // Crear nuevo registro local
        const newRecord = {
          study_environment: environmentId,
          suggested_students,
        };

        if (this.old_matter) {
          newRecord.matter = this.old_matter.id;
        } else if (this.matter) {
          newRecord.egress_profile_matter = this.matter.id;
        }

        // Agregar al array local sin hacer POST aún
        // El POST se hará en saveStudyEnvironments cuando matter.id sea válido
        dataArray.push(newRecord);
      }
    },
    async saveStudyEnvironments() {
      try {
        // Guardar para old_matter (endpoint: /mesh/matter-study-environment/)
        if (
          this.old_matter &&
          this.old_matter.id &&
          !isNaN(this.old_matter.id)
        ) {
          const selectedOldMatterIds = this.old_matter.study_environments || [];

          // Eliminar ambientes que ya no están seleccionados (comparar contra TODOS los originales)
          const toDeleteOldMatter = this.all_env_students_old_matter.filter(
            (env) =>
              env.id && !selectedOldMatterIds.includes(env.study_environment)
          );
          for (const env of toDeleteOldMatter) {
            await this.$store.dispatch(
              names.DELETE_MATTER_STUDY_ENVIRONMENT,
              env.id
            );
          }

          // Crear o actualizar ambientes seleccionados de old_matter (usar datos locales)
          const toSaveOldMatter = this.local_env_students_old_matter.filter(
            (env) => selectedOldMatterIds.includes(env.study_environment)
          );
          for (const envStudent of toSaveOldMatter) {
            const payload = {
              matter: this.old_matter.id,
              study_environment: envStudent.study_environment,
              suggested_students: envStudent.suggested_students,
            };

            if (envStudent.id) {
              await this.$store.dispatch(names.PATCH_MATTER_STUDY_ENVIRONMENT, {
                id: envStudent.id,
                data: payload,
              });
            } else {
              await this.$store.dispatch(
                names.POST_MATTER_STUDY_ENVIRONMENT,
                payload
              );
            }
          }
        }

        // Guardar para matter (endpoint: /mesh/egress-profile-matter-study-environment/)
        if (this.matter && this.matter.id && !isNaN(this.matter.id)) {
          const selectedMatterIds = this.matter.study_environments || [];

          // Eliminar ambientes que ya no están seleccionados (comparar contra TODOS los originales)
          const toDeleteMatter = this.all_env_students_matter.filter(
            (env) =>
              env.id && !selectedMatterIds.includes(env.study_environment)
          );
          for (const env of toDeleteMatter) {
            await this.$store.dispatch(
              names.DELETE_EGRESS_PROFILE_MATTER_STUDY_ENVIRONMENT,
              env.id
            );
          }

          // Crear o actualizar ambientes seleccionados de matter (usar datos locales)
          const toSaveMatter = this.local_env_students_matter.filter((env) =>
            selectedMatterIds.includes(env.study_environment)
          );
          for (const envStudent of toSaveMatter) {
            const payload = {
              egress_profile_matter: this.matter.id,
              study_environment: envStudent.study_environment,
              suggested_students: envStudent.suggested_students,
            };

            if (envStudent.id) {
              await this.$store.dispatch(
                names.PATCH_EGRESS_PROFILE_MATTER_STUDY_ENVIRONMENT,
                {
                  id: envStudent.id,
                  data: payload,
                }
              );
            } else {
              await this.$store.dispatch(
                names.POST_EGRESS_PROFILE_MATTER_STUDY_ENVIRONMENT,
                payload
              );
            }
          }
        }
      } catch (error) {
        console.error("Error guardando study environments:", error);
      }
    },
  },
  watch: {},
  async mounted() {
    // Cargar y guardar copia de los registros actuales para detectar eliminaciones
    const promises = [];

    if (
      this.matter &&
      !isNaN(this.matter.id) &&
      !this.matter.use_matter_values
    ) {
      const promise = this.$store
        .dispatch(
          names.FETCH_EGRESS_PROFILE_MATTER_STUDY_ENVIRONMENT,
          this.matter.id
        )
        .then((response) => {
          this.all_env_students_matter = JSON.parse(JSON.stringify(response));
          this.local_env_students_matter = JSON.parse(JSON.stringify(response));
        });
      promises.push(promise);
    } else {
      // Si es nueva asignatura, inicializar array vacío
      this.local_env_students_matter = [];
    }

    if (this.old_matter && !isNaN(this.old_matter.id)) {
      const promise = this.$store
        .dispatch(names.FETCH_MATTER_STUDY_ENVIRONMENT, this.old_matter.id)
        .then((response) => {
          this.all_env_students_old_matter = JSON.parse(
            JSON.stringify(response)
          );
          this.local_env_students_old_matter = JSON.parse(
            JSON.stringify(response)
          );
        });
      promises.push(promise);
    } else {
      // Si es nueva asignatura, inicializar array vacío
      this.local_env_students_old_matter = [];
    }

    await Promise.all(promises);
    this.isLoading = false;
  },
  created() {},
};
</script>

<style scoped>
.environment-name {
  font-size: var(--secondary-font-size) !important;
}
</style>